import React, { useState } from 'react';
import { X, Upload } from 'lucide-react';

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (csvText: string) => Promise<{ created: number; errors: number; }>;
  darkMode?: boolean;
}

const BulkUploadModal: React.FC<Props> = ({ open, onClose, onSubmit, darkMode = false }) => {
  const [text, setText] = useState('');
  const [result, setResult] = useState<{ created: number; errors: number; } | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  const handleFile = async (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      const content = (reader.result as string) || '';
      setText(content);
    };
    reader.readAsText(file);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await onSubmit(text.trim());
      setResult(res);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className={`${darkMode ? 'bg-[#232336] text-white border border-white/10' : 'bg-white'} rounded-2xl w-full max-w-2xl p-6 shadow-xl`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Bulk Add Users (CSV)</h3>
          <button onClick={onClose} aria-label="Close" className={`${darkMode ? 'text-white/70 hover:text-white' : ''}`}><X /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className={`block text-sm mb-2 ${darkMode ? 'text-white/70' : 'text-gray-700'}`}>Paste CSV (headers optional)</label>
            <textarea value={text} onChange={e => setText(e.target.value)} rows={8} className={`w-full border rounded-lg px-3 py-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-pink-500 ${darkMode ? 'bg-[#0f1420] border-white/10 text-white placeholder-white/40' : 'focus:border-transparent'}`}></textarea>
            <div className={`${darkMode ? 'text-white/60' : 'text-gray-500'} text-xs mt-2`}>
              Expected columns: firstName, lastName, email, phoneNumber, department, position, location (role optional)
            </div>
          </div>
          <div className="flex items-center justify-between gap-3">
            <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
              <input type="file" accept=".csv,text/csv" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }} className="hidden" />
              <span className={`px-3 py-1.5 rounded-lg border ${darkMode ? 'border-white/10 text-white/80 hover:bg-white/10' : ''}`}>Choose CSV</span>
            </label>
            <div className="flex items-center gap-3">
              <button type="button" className={`px-4 py-2 rounded-lg border ${darkMode ? 'border-white/10 text-white/80 hover:bg-white/10' : ''}`} onClick={onClose}>Close</button>
              <button type="submit" disabled={submitting} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-red-600 to-pink-600 text-white disabled:opacity-60">
                <Upload size={18} /> {submitting ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </div>
          {result && (
            <div className={`mt-2 text-sm ${darkMode ? 'text-white/80' : 'text-gray-700'}`}>
              Created {result.created} users; {result.errors} rows skipped.
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default BulkUploadModal; 